import { http } from '@/shared/api/api-client';

import type {
  CatalogueHealth,
  CatalogueHealthItem,
  ChannelDeletionCheck,
  ChannelDetailsPayload,
  CountryPricing,
  CountryReadiness,
  CountrySettingsPayload,
  Currency,
  FxHistory,
  FxRate,
  MarketCountry,
  PaymentChannel,
  PricePreview,
  PricingCalculation,
  PricingCalculationRequest,
  PricingHealth,
} from '../types/pricing.types';

/**
 * Markets and pricing calls — the Admin API re-checks the country against the
 * staff scope, audits every change, and activates pricing only through an
 * approval by someone else.
 */

export async function getCountries(signal?: AbortSignal): Promise<MarketCountry[]> {
  return http.get<MarketCountry[]>('/admin/countries', { signal });
}

export async function getCountry(countryId: number, signal?: AbortSignal): Promise<MarketCountry> {
  return http.get<MarketCountry>(`/admin/countries/${countryId}`, { signal });
}

export async function getCountryPricing(
  countryId: number,
  signal?: AbortSignal,
): Promise<CountryPricing> {
  return http.get<CountryPricing>(`/admin/countries/${countryId}/pricing`, { signal });
}

export async function setDraftParameter(
  countryId: number,
  parameter: string,
  value: number,
): Promise<CountryPricing> {
  return http.put<CountryPricing>(
    `/admin/countries/${countryId}/pricing/draft/${encodeURIComponent(parameter)}`,
    { value },
  );
}

/** Returns the approval request's id. */
export async function submitDraft(countryId: number, reason: string): Promise<number> {
  return http.post<number>(`/admin/countries/${countryId}/pricing/draft/submit`, { reason });
}

export async function decideApproval(
  approvalId: number,
  decision: 'approve' | 'reject' | 'cancel',
  reason: string | null,
): Promise<void> {
  await http.post<number>(`/admin/pricing/approvals/${approvalId}/${decision}`, { reason });
}

export async function getPaymentChannels(
  countryId: number,
  signal?: AbortSignal,
): Promise<PaymentChannel[]> {
  return http.get<PaymentChannel[]>(`/admin/countries/${countryId}/payment-channels`, { signal });
}

/** Whether the channel can be deleted, and why not (countries.manage). */
export async function getChannelDeletionCheck(
  countryId: number,
  channelId: number,
  signal?: AbortSignal,
): Promise<ChannelDeletionCheck> {
  return http.get<ChannelDeletionCheck>(
    `/admin/countries/${countryId}/payment-channels/${channelId}/deletion`,
    { signal },
  );
}

/**
 * Deletes a channel no order used (countries.manage). Irreversible, so the
 * reason is required; a channel in use is refused with 409 CHANNEL_IN_USE.
 */
export async function deletePaymentChannel(
  countryId: number,
  channelId: number,
  reason: string,
): Promise<void> {
  await http.delete<{ changed: number }>(
    `/admin/countries/${countryId}/payment-channels/${channelId}`,
    { data: { reason } },
  );
}

export async function setPaymentChannelStatus(
  countryId: number,
  channelId: number,
  isActive: boolean,
  reason: string | null,
): Promise<number> {
  const result = await http.post<{ changed: number }>(
    `/admin/countries/${countryId}/payment-channels/${channelId}/status`,
    { isActive, reason },
  );
  return result.changed;
}

export async function getFxRates(countryId: number, signal?: AbortSignal): Promise<FxRate[]> {
  return http.get<FxRate[]>(`/admin/countries/${countryId}/fx-rates`, { signal });
}

/**
 * How a country's completed orders were priced over a period, per brand —
 * the same backend pricing report the Reports page shows (reports.pricing.read).
 */
export async function getPricingHealth(
  countryId: number,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<PricingHealth> {
  return http.get<PricingHealth>('/admin/reports/pricing', {
    params: { countryId, from, to, groupBy: 'brand' },
    signal,
  });
}

export async function getCatalogueHealth(
  countryId: number,
  signal?: AbortSignal,
): Promise<CatalogueHealth> {
  return http.get<CatalogueHealth>(`/admin/countries/${countryId}/catalogue/health`, { signal });
}

/** The offered items catalogue health counts as not purchasable because of one reason. */
export async function getCatalogueHealthItems(
  countryId: number,
  reason: string,
  signal?: AbortSignal,
): Promise<CatalogueHealthItem[]> {
  return http.get<CatalogueHealthItem[]>(`/admin/countries/${countryId}/catalogue/health/items`, {
    params: { reason },
    signal,
  });
}

/** Pricing Calculation Inspector: the checkout engine's calculation for one item, explained (audited). */
export async function getPricingCalculation(
  request: PricingCalculationRequest,
  signal?: AbortSignal,
): Promise<PricingCalculation> {
  return http.post<PricingCalculation>('/admin/pricing/calculation', request, { signal });
}

export async function getPricePreview(
  countryId: number,
  denominationId: number,
  includeDraft: boolean,
  signal?: AbortSignal,
): Promise<PricePreview> {
  return http.post<PricePreview>(
    `/admin/countries/${countryId}/pricing/preview`,
    { denominationId, includeDraft },
    { signal },
  );
}

// ------------------------------------------------- country settings (P2)

export async function getCountryReadiness(
  countryId: number,
  signal?: AbortSignal,
): Promise<CountryReadiness> {
  return http.get<CountryReadiness>(`/admin/countries/${countryId}/readiness`, { signal });
}

/** Currencies change only while the country is inactive (COUNTRY_CURRENCY_LOCKED). */
export async function updateCountrySettings(
  countryId: number,
  payload: CountrySettingsPayload,
): Promise<void> {
  await http.put(`/admin/countries/${countryId}/settings`, payload);
}

/** Activation is refused unless ready; deactivation needs the English name typed back. */
export async function setCountryStatus(
  countryId: number,
  input: { isActive: boolean; reason: string; confirmName: string | null },
): Promise<void> {
  await http.post(`/admin/countries/${countryId}/status`, input);
}

// -------------------------------------------------------------- currencies

export async function getCurrencies(signal?: AbortSignal): Promise<Currency[]> {
  return http.get<Currency[]>('/admin/currencies', { signal });
}

export async function createCurrency(payload: {
  code: string;
  name: string;
  symbol: string | null;
  reason: string;
}): Promise<string> {
  return http.post<string>('/admin/currencies', payload);
}

export async function updateCurrency(
  code: string,
  payload: { name: string; symbol: string | null; reason: string },
): Promise<void> {
  await http.put(`/admin/currencies/${encodeURIComponent(code)}`, payload);
}

/** Deactivating a currency in use needs acknowledgeUsage (CURRENCY_IN_USE otherwise). */
export async function setCurrencyStatus(
  code: string,
  input: { isActive: boolean; reason: string; acknowledgeUsage: boolean },
): Promise<void> {
  await http.post(`/admin/currencies/${encodeURIComponent(code)}/status`, input);
}

/** Feed rates for the last `days` days, manual rates, and a pending request (pricing.read). */
export async function getFxHistory(
  countryId: number,
  currencyCode: string,
  days: number,
  signal?: AbortSignal,
): Promise<FxHistory> {
  return http.get<FxHistory>(`/admin/countries/${countryId}/fx-rates/${currencyCode}/history`, {
    params: { days },
    signal,
  });
}

/**
 * Asks for a manual rate (units per 1 USD) for `durationHours` from approval
 * (fx.override.request). An Admin approves it; it changes prices in every
 * country using the currency. Returns the approval id.
 */
export async function requestFxOverride(
  countryId: number,
  currencyCode: string,
  payload: { rate: number; durationHours: number; reason: string },
): Promise<number> {
  return http.post<number>(
    `/admin/countries/${countryId}/fx-rates/${currencyCode}/override`,
    payload,
  );
}

/** Ends the manual rate now; the feed applies again at once. */
export async function revokeFxOverride(countryId: number, currencyCode: string, reason: string) {
  return http.post<boolean>(
    `/admin/countries/${countryId}/fx-rates/${currencyCode}/override/revoke`,
    { reason },
  );
}

/** Names, icon and position; applied at once and audited (pricing.config.manage). */
export async function updatePaymentChannel(
  countryId: number,
  channelId: number,
  payload: ChannelDetailsPayload,
) {
  return http.put<{ changed: number }>(
    `/admin/countries/${countryId}/payment-channels/${channelId}`,
    payload,
  );
}

/**
 * Saves the country's checkout order (pricing.config.manage): every channel of
 * the country, active or not, first to last. The backend numbers them 1..n and
 * audits it; a stale or incomplete list is refused with 409
 * CHANNEL_ORDER_INCOMPLETE. Returns 0 when it was already the saved order.
 */
export async function saveChannelOrder(
  countryId: number,
  channelIds: number[],
  reason: string,
): Promise<number> {
  const result = await http.put<{ changed: number }>(
    `/admin/countries/${countryId}/payment-channels/order`,
    { channelIds, reason },
  );
  return result.changed;
}

/** New fees change the customer price: this only asks, an Admin approves. Returns the approval id. */
export async function requestChannelFees(
  countryId: number,
  channelId: number,
  payload: { percentFee: number; fixedFee: number; reason: string },
): Promise<number> {
  return http.post<number>(
    `/admin/countries/${countryId}/payment-channels/${channelId}/fees`,
    payload,
  );
}
